import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, MessageSquare, UserMinus, UserPlus, Check, Clock, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { countryFlag } from "@/lib/places";
import { toast } from "sonner";

export default function FriendsPage() {
  const nav = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  useEffect(() => { if (user && user !== false) api.get("/friends").then((r) => setFriends(r.data)).catch(() => {}); }, [user]);
  useEffect(() => {
    if (tab !== "search") return;
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, tab]);

  if (!user || user === false) return <div className="p-8 text-center text-slate-500">Kyçu për të parë shoqërinë.</div>;

  const removeFriend = async (id) => { if (!(await confirm({ title: "Hiq nga shoqëria", message: "Ta heqim këtë person nga shoqëria?" }))) return; await api.delete(`/friends/${id}`); setFriends((f) => f.filter((x) => x.id !== id)); toast.success("U hoq"); };
  const addFriend = async (id) => { await api.post(`/friends/request/${id}`); setResults((rs) => rs.map((x) => x.id === id ? { ...x, relationship: "pending_out" } : x)); toast.success("Kërkesa u dërgua"); };

  const relBtn = (p) => {
    if (p.relationship === "friends") return <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" />Miq</span>;
    if (p.relationship === "pending_out") return <span className="text-xs font-semibold text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Dërguar</span>;
    if (p.relationship === "pending_in") return <button data-testid={`accept-from-${p.id}`} onClick={() => nav("/kerkesat")} className="text-xs font-bold text-emerald-600">Përgjigju</button>;
    return <button data-testid={`add-friend-${p.id}`} onClick={() => addFriend(p.id)} className="text-xs font-bold text-white bg-emerald-500 rounded-lg px-2.5 py-1.5 flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" />Shto</button>;
  };

  const Row = ({ p, actions }) => (
    <div className="flex items-center gap-3 bg-white rounded-2xl p-2.5 border border-slate-100">
      <button onClick={() => nav(`/perdorues/${p.id}`)} className="relative w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold overflow-hidden shrink-0">
        {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" /> : (p.name?.[0] || "?")}
        <span data-testid={`online-${p.id}`} className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white rounded-full ${p.online ? "bg-emerald-500" : "bg-slate-300"}`} />
      </button>
      <div className="flex-1 min-w-0" onClick={() => nav(`/perdorues/${p.id}`)}>
        <div className="flex items-center gap-1"><span className="font-semibold text-sm text-slate-800 truncate">{p.name} {p.surname}</span>{p.verified && <BadgeCheck className="w-4 h-4 text-sky-500" />}</div>
        <div className="text-xs text-slate-400 truncate">{countryFlag(p.country)} {p.city}</div>
      </div>
      {actions}
    </div>
  );

  return (
    <div className="gs-fade px-4 pt-4 pb-6" data-testid="friends-page">
      <button onClick={() => nav("/profil")} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />Profili</button>
      <h1 className="text-2xl font-extrabold text-slate-900 mb-3">Shoqëria ime</h1>

      <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-xl p-1 mb-4">
        <button data-testid="tab-friends" onClick={() => setTab("friends")} className={`py-2 rounded-lg text-sm font-semibold ${tab === "friends" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Miqtë ({friends.length})</button>
        <button data-testid="tab-search" onClick={() => setTab("search")} className={`py-2 rounded-lg text-sm font-semibold ${tab === "search" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Kërko persona</button>
      </div>

      {tab === "friends" ? (
        <div className="space-y-2">
          {friends.map((p) => <Row key={p.id} p={p} actions={<>
            <button data-testid={`msg-${p.id}`} onClick={() => nav(`/mesazhe/${p.id}`)} className="text-sky-500 p-2"><MessageSquare className="w-4 h-4" /></button>
            <button data-testid={`remove-${p.id}`} onClick={() => removeFriend(p.id)} className="text-red-400 p-2"><UserMinus className="w-4 h-4" /></button>
          </>} />)}
          {!friends.length && <p className="text-center text-slate-400 py-10 text-sm">Ende pa shoqëri. Kërko persona për të filluar.</p>}
        </div>
      ) : (
        <div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="search-people-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kërko me emër ose nofkë..." className="w-full bg-slate-100 rounded-xl h-11 pl-9 pr-3 text-sm outline-none" />
          </div>
          {!q && <p className="text-xs text-slate-400 mb-2">Personat më të afërt me ty</p>}
          <div className="space-y-2">
            {results.map((p) => <Row key={p.id} p={p} actions={relBtn(p)} />)}
            {!results.length && <p className="text-center text-slate-400 py-8 text-sm">Asnjë person për të shfaqur.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
